import React, { useState, useEffect, useRef } from "react";
import SideBar from "../inc/SideBar";
import { Container, Dropdown, Image, Form } from "react-bootstrap";
import { FiMoreVertical, FiMenu, FiPlusCircle, FiArrowLeft } from "react-icons/fi";
import { FaRegEye, FaRegEdit, FaRegTrashAlt, FaCalendarAlt } from "react-icons/fa";
import { IoIosCloseCircleOutline } from "react-icons/io";
import SearchIcon from "../../web_components/images/search.png";
import FilterIcon from "../images/filter.svg";
import ExportIcon from "../images/exporticon.svg";
import AxiosConfig from "../services/AxiosConfig";
import { IMAGE_URL } from "../services/env";
import "./Claims.scss";
import NotificationIcon from "../images/notification.svg";
import { Link } from "react-router-dom";
import { Badge, Button } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PillSelect from "../customcomponents/PillSelect";
import { toDisplayDate, toInputDate, todayDisplayDate, toComparableDate } from "../services/dateFormat";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import ClaimsFilterSidebar from "../inc/ClaimsFilterSidebar";

const ANOMALY_CATEGORIES = [
  "Damage",
  "Loss",
  "Loading delay",
  "Delivery delay",
  "Missed",
  "Tracking/visibility",
  "Billing",
  "Non compliance",
];

const CLAIM_STATUS_OPTIONS = ["Pending", "Closed", "Rejected", "Resolved"];

const Claims = () => {
  const isUploadedClaimFile = (file) => typeof file === "string";

  const getClaimFileLabel = (file) => {
    const rawName = isUploadedClaimFile(file)
      ? file.split("/").pop()
      : file.name;
    // Strip server timestamp prefix: 1784151576535_MSDS.pdf → MSDS.pdf
    return String(rawName || "").replace(/^\d+_/, "");
  };

  const getClaimFileUrl = (filePath) => `${IMAGE_URL}server/${filePath}`;

  const renderClaimReference = (reference) => {
    if (!reference) return "—";
    return String(reference);
  };

  const getNextClaimReferencePreview = (claimsList = []) => {
    let maxSeq = 0;
    claimsList.forEach((claim) => {
      const match = String(claim.reference || "").match(/^82(\d{4})$/);
      if (match) {
        maxSeq = Math.max(maxSeq, parseInt(match[1], 10));
      }
    });
    return `82${String(maxSeq + 1).padStart(4, "0")}`;
  };

  const uploadClaimFilesToServer = async (fileList) => {
    const uploadFormData = new FormData();
    fileList.forEach((file) => uploadFormData.append("file", file));
    const response = await AxiosConfig.instance.post("/upload_claim_files", uploadFormData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  };

  const resolveClaimFiles = async (files = []) => {
    const existingPaths = files.filter(isUploadedClaimFile);
    const pendingFiles = files.filter((file) => file instanceof File);

    if (pendingFiles.length === 0) {
      return existingPaths;
    }

    const uploadRes = await uploadClaimFilesToServer(pendingFiles);
    if (!uploadRes.success) {
      throw new Error(uploadRes.message || "File upload failed.");
    }

    return [...existingPaths, ...(uploadRes.data || [])];
  };

  const fileInputRef = useRef(null);
  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFormData(prev => ({...prev, files: [...(prev.files || []), ...newFiles]}));
  };
  const userDetail = JSON.parse(localStorage.getItem("userDetail") || "{}");
  const userRole = userDetail.profile;
  const currentUserId = userDetail._id || "";
  const isStaffOnly = userRole === "Staff";
  const isRoleStaff = userRole === "Staff" || userRole === "Admin";
  const isRoleClient = userRole === "Client" || userRole === "User";
  const isRoleCarrier = userRole === "Carrier";
  const currentStaffAccountNumber = (userDetail.accountNumber || "").trim();
  const requiresStaffAccountForPlatform = isStaffOnly || userRole === "Admin";
  const canCreateClaim = !isRoleCarrier;
  const canManageClaims = !isRoleCarrier;
  const canEditClaimItem = (claim) => {
    // Carrier only edits claims transferred to them (after Platform verification)
    if (isRoleCarrier) {
      return claim?.assignTo === "Carrier" && claim?.owner !== "Platform";
    }
    return canManageClaims;
  };

  /** Owner display: Platform, Carrier, or Shipper (legacy Client) — for form/destination */
  const displayClaimOwner = (owner) => {
    if (owner === "Platform") return "Platform";
    if (owner === "Carrier") return "Carrier";
    return "Shipper";
  };

  /**
   * List column "Raised by": who opened the claim — Shipper or Platform only (never Carrier).
   * Prefer createdByRole; fall back without showing Carrier as raiser.
   * Used for Carrier & Staff list — NOT for Shipper (see displayListPartyColumn).
   */
  const displayRaisedBy = (claim) => {
    const role = claim?.createdByRole;
    if (role === "Staff" || role === "Admin") return "Platform";
    if (
      role === "Client" ||
      role === "User" ||
      role === "Shipper"
    ) {
      return "Shipper";
    }
    if (claim?.owner === "Platform") return "Platform";
    return "Shipper";
  };

  /**
   * Shipper list only: column "Owner" = Platform | Carrier (destination).
   * Carrier / Staff keep "Raised by" — do not reuse Shipper model.
   */
  const listPartyColumnLabel = isRoleClient ? "OWNER" : "RAISED BY";
  const listPartyExportLabel = isRoleClient ? "Owner" : "Raised by";
  const displayListPartyColumn = (claim) => {
    if (isRoleClient) {
      if (claim?.owner === "Platform") return "Platform";
      return "Carrier";
    }
    return displayRaisedBy(claim);
  };

  /** Normalize stored owner for the form */
  const normalizeClaimOwnerValue = (owner) => {
    if (owner === "Platform") return "Platform";
    if (owner === "Carrier") return "Carrier";
    return "Client";
  };

  /** Client/Shipper-owned (or destined to Carrier) claims can be transferred to Carrier after Platform review */
  const canTransferToCarrier = (owner) =>
    owner === "Client" || owner === "Carrier" || owner === "Shipper";

  /** RCA + Corrective: Carrier only. Staff/Shipper (openers) → Description + Expected only. */
  const isClaimReceiverForForm = (assignTo, isEditing) => {
    if (!isEditing) return false;
    if (isRoleCarrier && assignTo === "Carrier") return true;
    return false;
  };

  /** Assign options: Carrier → Platform only; Staff → Platform / Carrier / Shipper (no empty "Select…" placeholder) */
  const getAssignToOptions = (ownerValue) => {
    if (isRoleCarrier) {
      return [{ value: "Platform", label: "Platform" }];
    }
    // Platform-owned claims stay on Platform — never transfer to Carrier
    if (ownerValue === "Platform") {
      return [{ value: "Platform", label: "Platform" }];
    }
    const options = [{ value: "Platform", label: "Platform" }];
    if (isRoleStaff) {
      options.push({ value: "Carrier", label: "Carrier" });
      options.push({ value: "Client", label: "Shipper" });
    }
    return options;
  };

  const displayAssignToLabel = (assignTo) => {
    if (assignTo === "Client" || assignTo === "Shipper") return "Shipper";
    // Carrier UI: never show "Carrier" as Assign to — display Platform
    if (assignTo === "Carrier") return isRoleCarrier ? "Platform" : "Carrier";
    return assignTo || "Platform";
  };

  // Client/Shipper: Owner = Carrier | Platform
  // Staff: Owner = Carrier only
  const getOwnerOptions = () => {
    if (isRoleClient) {
      return [
        { value: "Carrier", label: "Carrier" },
        { value: "Platform", label: "Platform" },
      ];
    }
    if (isRoleStaff) {
      return [{ value: "Carrier", label: "Carrier" }];
    }
    return [
      { value: "Client", label: "Shipper" },
      { value: "Platform", label: "Platform" },
    ];
  };
  const [view, setView] = useState("list"); // 'list', 'new', 'view'
  const [editingClaimId, setEditingClaimId] = useState(null);
  const [currentViewClaim, setCurrentViewClaim] = useState(null);
  const [claims, setClaims] = useState([]);
  const [stats, setStats] = useState({
    totalClaims: 0,
    resolved: 0,
    pending: 0,
    highPriority: 0,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [isBodyClassActive, setIsBodyClassActive] = useState(false);
  const [exportMode, setExportMode] = useState(false);
  const [selectedClaimIds, setSelectedClaimIds] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});

  const emptyClaimForm = () => ({
    shipment: "",
    owner: isRoleClient || isRoleStaff ? "Carrier" : "Client",
    category: "Damage",
    priority: "High",
    status: "Pending",
    fromDate: "",
    toDate: "",
    description: "",
    expectedActions: "",
    rootCauseAnalysis: "",
    correctiveActions: "",
    cmrRemark: false,
    assignTo: "Platform",
    staffAccountNumber: "",
    customer: "",
    targetCarrierId: "",
    files: [],
  });

  // New Claim Form State
  // Client opens → always routed to Platform first (verification / destination)
  const [formData, setFormData] = useState(emptyClaimForm);
  const [activeTab, setActiveTab] = useState("Description");
  const [saveError, setSaveError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Opener (Shipper / Platform Staff) = edit Description + Expected only
  // Receiver (Carrier only) = edit Root cause + Corrective; can VIEW Description + Expected (read-only)
  // For Carrier edit: keep receiver rights based on the claim as opened (still assigned to them),
  // even if they change Assign to Platform before save.
  const receiverAssignContext =
    isRoleCarrier && editingClaimId && currentViewClaim
      ? currentViewClaim.assignTo || "Carrier"
      : formData.assignTo;
  const canEditOpenerSections = !isClaimReceiverForForm(
    receiverAssignContext,
    Boolean(editingClaimId)
  );
  const canEditReceiverSections = isClaimReceiverForForm(
    receiverAssignContext,
    Boolean(editingClaimId)
  );
  // Once created: shipment, owner, category, priority, from/to are read-only
  const isClaimMetaLocked = Boolean(editingClaimId);

  const fetchClaims = async () => {
    try {
      const response = await AxiosConfig.instance.get("/get_claims", {
        params: {
          requesterRole: userRole,
          requesterId: currentUserId,
        },
      });
      if (response.data.success) {
        setClaims(response.data.data);
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error("Error fetching claims:", error);
    }
  };

  useEffect(() => {
    fetchClaims();
  }, []);

  useEffect(() => {
    if (filterOpen) {
      document.body.classList.add("filter_sidebar");
    } else {
      document.body.classList.remove("filter_sidebar");
    }
    return () => document.body.classList.remove("filter_sidebar");
  }, [filterOpen]);

  const toggleBodyClass = () => {
    setIsBodyClassActive(!isBodyClassActive);
  };

  const handleFilterToggle = () => {
    setFilterOpen((prev) => !prev);
  };

  const handleSideFilter = (filterItems) => {
    setActiveFilters(filterItems || {});
  };

  const matchesText = (value, query) => {
    if (!query || !String(query).trim()) return true;
    return String(value || "")
      .toLowerCase()
      .includes(String(query).trim().toLowerCase());
  };

  const matchesDateRange = (rawDate, dateFrom, dateTo) => {
    if (!dateFrom && !dateTo) return true;
    const current = toComparableDate(rawDate);
    if (!current) return false;
    if (dateFrom) {
      const from = toComparableDate(dateFrom);
      if (from && current < from) return false;
    }
    if (dateTo) {
      const to = toComparableDate(dateTo);
      if (to && current > to) return false;
    }
    return true;
  };

  const filteredClaims = claims.filter((clm) => {
    const searchOk =
      !searchQuery ||
      clm.reference?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      clm.shipment?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      clm.category?.toLowerCase().includes(searchQuery.toLowerCase());

    if (!searchOk) return false;

    const f = activeFilters;
    if (!f || Object.keys(f).length === 0) return true;

    if (!matchesText(clm.reference, f.reference)) return false;
    if (!matchesText(clm.status, f.status)) return false;
    if (!matchesDateRange(clm.date, f.dateFrom, f.dateTo)) return false;
    if (!matchesText(clm.category, f.category)) return false;
    if (!matchesText(clm.shipment, f.shipment)) return false;
    if (!matchesText(displayListPartyColumn(clm), f.owner)) return false;
    if (!matchesText(clm.priority, f.priority)) return false;

    return true;
  });

  const selectedClaimsCount = selectedClaimIds.length;
  const allVisibleClaimsSelected =
    filteredClaims.length > 0 &&
    filteredClaims.every((clm) => selectedClaimIds.includes(clm._id));

  const toggleExportMode = () => {
    setExportMode((prev) => {
      if (prev) setSelectedClaimIds([]);
      return !prev;
    });
  };

  const handleSelectAllClaims = (checked) => {
    if (checked) {
      setSelectedClaimIds(filteredClaims.map((clm) => clm._id).filter(Boolean));
    } else {
      setSelectedClaimIds([]);
    }
  };

  const handleSelectClaimRow = (claimId) => {
    if (!claimId) return;
    setSelectedClaimIds((prev) =>
      prev.includes(claimId)
        ? prev.filter((id) => id !== claimId)
        : [...prev, claimId]
    );
  };

  const handleExportClaimsToExcel = async () => {
    if (selectedClaimsCount === 0) {
      toast.error("Please select element");
      return;
    }

    const selectedClaims = filteredClaims.filter((clm) =>
      selectedClaimIds.includes(clm._id)
    );

    // Shipper: Owner (Platform|Carrier). Carrier/Staff: Raised by (Shipper|Platform)
    const headers = [
      "Reference",
      "Status",
      "Date",
      "Category",
      "Shipment",
      listPartyExportLabel,
      "Priority",
    ];

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Claims");
    worksheet.addRow(headers);

    selectedClaims.forEach((clm) => {
      worksheet.addRow([
        clm.reference || "",
        clm.status || "",
        toDisplayDate(clm.date) || clm.date || "",
        clm.category || "",
        clm.shipment || "",
        displayListPartyColumn(clm),
        clm.priority || "",
      ]);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "claims.xlsx");
    toast.success("Claims exported successfully.");
    setSelectedClaimIds([]);
    setExportMode(false);
  };

  const handleRemoveFile = (index) => {
    setFormData((prev) => ({
      ...prev,
      files: (prev.files || []).filter((_, i) => i !== index),
    }));
  };

  const renderClaimFileBadge = (file, index, { removable = false } = {}) => {
    const label = getClaimFileLabel(file);
    const content = isUploadedClaimFile(file) ? (
      <a
        href={getClaimFileUrl(file)}
        target="_blank"
        rel="noopener noreferrer"
        className="file-badge file-badge-link"
      >
        {label}
      </a>
    ) : (
      <span className="file-badge">{label}</span>
    );

    return (
      <span key={`${label}-${index}`} className="file-badge-wrap">
        {content}
        {removable && (
          <button
            type="button"
            className="file-remove-btn"
            onClick={() => handleRemoveFile(index)}
            aria-label={`Supprimer ${label}`}
          >
            ×
          </button>
        )}
      </span>
    );
  };
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const next = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };
      if (name === "assignTo" && value !== "Platform") {
        next.staffAccountNumber = "";
      }
      // Client/shipper: claim always goes to Platform first (owner Platform or Client pending review)
      if (name === "owner" && isRoleClient) {
        next.assignTo = "Platform";
        next.staffAccountNumber = "";
      }
      if (name === "owner" && value === "Platform") {
        next.assignTo = "Platform";
        next.staffAccountNumber = "";
      }
      return next;
    });
    if (name === "assignTo" || name === "staffAccountNumber" || name === "owner") {
      setSaveError("");
    }
  };

  const normalizeDateForInput = (rawDate) => toInputDate(rawDate);

  const handleShipmentLookup = async () => {
    if (canEditReceiverSections) {
      toast.info("Shipment autofill is only available for the claim author fields.");
      return;
    }
    if (isClaimMetaLocked) {
      toast.info("Shipment number is locked on an existing claim.");
      return;
    }
    const shipmentRef = (formData.shipment || "").trim();
    if (!shipmentRef) {
      toast.error("Please enter a shipment number.");
      return;
    }

    try {
      const response = await AxiosConfig.instance.get(
        `/get_quote_by_reference/${encodeURIComponent(shipmentRef)}`
      );
      if (!response.data.success || !response.data.data) {
        toast.error("Shipment number not found. Please check the shipment number.");
        return;
      }

      const quote = response.data.data;
      const loading = quote.LOADING_INFORMATIONS?.[0] || {};
      const delivery = quote.DELIVERY_INFORMATIONS?.[0] || {};
      const approvedCarrier = (quote.ChooseCarrier || []).find(
        (carrier) => carrier.Status === "Approved"
      );

      let owner = formData.owner;
      let targetCarrierId = "";
      // Client keeps Carrier|Platform; Staff owner stays Carrier when a carrier exists on shipment
      if (approvedCarrier || quote.approved_carrier_id || quote.carrier) {
        if (isRoleClient) {
          if (owner !== "Platform" && owner !== "Carrier") owner = "Carrier";
        } else if (isRoleStaff) {
          owner = "Carrier";
        } else if (owner !== "Platform") {
          owner = "Client";
        }
        targetCarrierId =
          approvedCarrier?.carrierId ||
          quote.approved_carrier_id ||
          "";
      } else if (quote.UserType === "Client" || quote.client_id || quote.UserType === "User") {
        if (isRoleStaff) owner = "Carrier";
      }

      setFormData((prev) => ({
        ...prev,
        fromDate: normalizeDateForInput(loading.Date) || prev.fromDate,
        toDate:
          normalizeDateForInput(delivery.delivery_Date || delivery.Date) ||
          prev.toDate,
        owner: owner || prev.owner,
        assignTo: isRoleClient ? "Platform" : prev.assignTo,
        targetCarrierId: targetCarrierId || prev.targetCarrierId || "",
        customer:
          loading.Company_name ||
          quote.applicant ||
          quote.carrier ||
          prev.customer ||
          "",
      }));
      toast.success("Shipment details loaded.");
    } catch (error) {
      console.error("Error fetching shipment info:", error);
      toast.error("Shipment number not found. Please check the shipment number.");
    }
  };

  const validatePlatformAssign = () => {
    if (formData.assignTo !== "Platform" || !requiresStaffAccountForPlatform) {
      setSaveError("");
      return true;
    }

    const staffAccount = (formData.staffAccountNumber || "").trim();
    if (!staffAccount) {
      const message = "Please enter the account number of the relevant staff member.";
      setSaveError(message);
      toast.error(message);
      return false;
    }

    if (isStaffOnly && staffAccount === currentStaffAccountNumber) {
      const message =
        "You cannot assign the claim to yourself. Enter another staff member's account number.";
      setSaveError(message);
      toast.error(message);
      return false;
    }

    setSaveError("");
    return true;
  };

  const buildClaimPayload = (filePaths = []) => {
    const baseMeta = {
      requesterRole: userRole,
      requesterAccountNumber: currentStaffAccountNumber,
      requesterId: currentUserId,
    };

    // Carrier only: Root cause + Corrective (+ Assign to Platform)
    if (canEditReceiverSections) {
      return {
        ...baseMeta,
        rootCauseAnalysis: formData.rootCauseAnalysis,
        correctiveActions: formData.correctiveActions,
        assignTo: "Platform",
        staffAccountNumber: formData.staffAccountNumber,
      };
    }

    // Claim author: Description + Expected (+ metadata only on create)
    // Client claims always route to Platform first (verification / destination)
    const resolvedAssignTo = isRoleClient
      ? "Platform"
      : formData.owner === "Platform"
        ? "Platform"
        : formData.assignTo;

    const payload = {
      ...baseMeta,
      description: formData.description,
      expectedActions: formData.expectedActions,
      cmrRemark: formData.cmrRemark,
      assignTo: resolvedAssignTo,
      staffAccountNumber:
        resolvedAssignTo === "Platform" ? formData.staffAccountNumber : "",
      platformResponse: formData.platformResponse,
      customer: formData.customer,
      files: filePaths,
      date: todayDisplayDate(),
    };

    if (isRoleStaff) {
      payload.status = formData.status || "Pending";
    }

    if (!editingClaimId) {
      payload.shipment = formData.shipment;
      if (formData.owner === "Platform") {
        payload.owner = "Platform";
      } else if (formData.owner === "Carrier") {
        payload.owner = "Carrier";
      } else {
        payload.owner = "Client";
      }
      payload.category = formData.category;
      payload.priority = formData.priority;
      payload.fromDate = toDisplayDate(formData.fromDate);
      payload.toDate = toDisplayDate(formData.toDate);
      if (formData.targetCarrierId) {
        payload.targetCarrierId = formData.targetCarrierId;
      }
    }

    return payload;
  };

  const handleViewClaim = (claim) => {
    setCurrentViewClaim(claim);
    setFormData({
      ...formData,
      platformResponse: claim.platformResponse || "",
      assignTo: claim.assignTo || claim.owner || "Carrier",
      staffAccountNumber: claim.staffAccountNumber || "",
    });
    setView("view");
  };

  const handleEditClaim = (claim) => {
    setEditingClaimId(claim._id);
    setCurrentViewClaim(claim);
    setFormData({
      shipment: claim.shipment || "",
      owner: normalizeClaimOwnerValue(claim.owner),
      category: claim.category || "Damage",
      priority: claim.priority || "High",
      status: claim.status || "Pending",
      fromDate: toInputDate(claim.fromDate),
      toDate: toInputDate(claim.toDate),
      description: claim.description || "",
      expectedActions: claim.expectedActions || "",
      rootCauseAnalysis: claim.rootCauseAnalysis || "",
      correctiveActions: claim.correctiveActions || "",
      cmrRemark: claim.cmrRemark || false,
      assignTo: claim.assignTo || "Platform",
      staffAccountNumber: claim.staffAccountNumber || "",
      files: claim.files || [],
      platformResponse: claim.platformResponse || "",
      customer: claim.customer || "",
      targetCarrierId: claim.targetCarrierId || "",
    });
    const isReceiver = isClaimReceiverForForm(
      claim.assignTo || "Platform",
      true
    );
    setActiveTab(isReceiver ? "Root cause analysis" : "Description");
    setView("new");
  };

  const handleDeleteClaim = async (id) => {
    if (!window.confirm("Are you sure you want to delete this claim?")) return;
    try {
      const response = await AxiosConfig.instance.post(`/delete_claim/${id}`, {
        requesterRole: userRole,
        requesterId: currentUserId,
      });
      if (response.data.success) {
        fetchClaims();
      }
    } catch (error) {
      console.error("Error deleting claims:", error);
    }
  };

  const handleSaveClaim = async () => {
    if (!validatePlatformAssign()) return;

    setIsSaving(true);
    setSaveError("");

    try {
      const filePaths = await resolveClaimFiles(formData.files || []);
      const payload = buildClaimPayload(filePaths);

      let response;
      if (editingClaimId) {
        response = await AxiosConfig.instance.post(`/update_claim/${editingClaimId}`, payload);
      } else {
        response = await AxiosConfig.instance.post("/add_claim", payload);
      }

      if (response.data.success) {
        toast.success("Claim saved successfully.");
        setView("list");
        fetchClaims();
        setFormData(emptyClaimForm());
        setEditingClaimId(null);
      } else if (response.data.message) {
        setSaveError(response.data.message);
        toast.error(response.data.message);
      }
    } catch (error) {
      const message = error.response?.data?.message || "Error while saving the claim.";
      setSaveError(message);
      toast.error(message);
      console.error("Error saving claim:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleTabChange = (tabName) => {
    // All roles can view every section; edit rights are enforced on the fields only
    setActiveTab(tabName);
  };

  const renderStaffAccountField = (readOnly = false) => {
    if (formData.assignTo !== "Platform" || !requiresStaffAccountForPlatform) {
      return null;
    }

    const fieldStyle = {
      width: "300px",
      borderRadius: "30px",
      border: "1px solid #e1e5ee",
      padding: "12px 20px",
      fontSize: "14px",
      color: "#7f8fa6",
      outline: "none",
    };

    return (
      <div style={{ marginTop: "16px" }}>
        <label style={{ display: "block", color: "#4f5b92", fontWeight: 600, marginBottom: "10px", fontSize: "14px" }}>
          Staff account number
        </label>
        {readOnly ? (
          <div className="readonly-box">{formData.staffAccountNumber || "N/A"}</div>
        ) : (
          <input
            type="text"
            name="staffAccountNumber"
            placeholder="Enter staff account number"
            value={formData.staffAccountNumber || ""}
            onChange={handleInputChange}
            style={fieldStyle}
          />
        )}
        {!readOnly && (
          <p style={{ marginTop: "8px", fontSize: "12px", color: "#7f8fa6" }}>
            Enter another staff member's account number (not yours
            {currentStaffAccountNumber ? ` : ${currentStaffAccountNumber}` : ""}).
          </p>
        )}
        {!readOnly && isStaffOnly && (formData.staffAccountNumber || "").trim() === currentStaffAccountNumber && currentStaffAccountNumber && (
          <p style={{ marginTop: "6px", fontSize: "12px", color: "#e74c3c", fontWeight: 600 }}>
            This is your own account number — please choose another staff member.
          </p>
        )}
      </div>
    );
  };

  const renderActiveTabContent = () => {
    const emptyHint = (editablePlaceholder, emptyReadOnlyHint) =>
      editablePlaceholder
        ? editablePlaceholder
        : emptyReadOnlyHint;

    switch (activeTab) {
      case "Description":
        return (
          <textarea
            name="description"
            placeholder={emptyHint(
              canEditOpenerSections ? "Enter your description" : null,
              "No description provided"
            )}
            value={formData.description}
            onChange={handleInputChange}
            readOnly={!canEditOpenerSections}
            className={!canEditOpenerSections ? "tab-field-readonly" : ""}
          ></textarea>
        );
      case "Expected actions":
        return (
          <textarea
            name="expectedActions"
            placeholder={emptyHint(
              canEditOpenerSections ? "Enter expected actions" : null,
              "No expected actions provided"
            )}
            value={formData.expectedActions}
            onChange={handleInputChange}
            readOnly={!canEditOpenerSections}
            className={!canEditOpenerSections ? "tab-field-readonly" : ""}
          ></textarea>
        );
      case "Root cause analysis":
        return (
          <textarea
            name="rootCauseAnalysis"
            placeholder={emptyHint(
              canEditReceiverSections ? "Enter root cause analysis" : null,
              "No root cause analysis provided"
            )}
            value={formData.rootCauseAnalysis}
            onChange={handleInputChange}
            readOnly={!canEditReceiverSections}
            className={!canEditReceiverSections ? "tab-field-readonly" : ""}
          ></textarea>
        );
      case "Corrective actions":
        return (
          <textarea
            name="correctiveActions"
            placeholder={emptyHint(
              canEditReceiverSections ? "Enter corrective actions" : null,
              "No corrective actions provided"
            )}
            value={formData.correctiveActions}
            onChange={handleInputChange}
            readOnly={!canEditReceiverSections}
            className={!canEditReceiverSections ? "tab-field-readonly" : ""}
          ></textarea>
        );
      default:
        return null;
    }
  };

  const renderViewClaim = () => {
    if (!currentViewClaim) return null;
    const access = localStorage.getItem("access");
    const isAdmin = access && access.includes("adminAccess");
    const isStaffAccess = access && access.includes("staffAccess");
    const showAssignInfo = isAdmin || isStaffAccess || isRoleCarrier;
    const assignToValue = currentViewClaim.assignTo || formData.assignTo || "Platform";

    const goBackToList = () => {
      setView("list");
      setEditingClaimId(null);
      setCurrentViewClaim(null);
    };

    return (
      <div className="claim-view-container">
        <div className="view-header">
          <h3>Claim Number : <span>{renderClaimReference(currentViewClaim.reference)}</span></h3>
          <h3>Date : <span>{toDisplayDate(currentViewClaim.date) || currentViewClaim.date}</span></h3>
        </div>

        <div className="info-grid">
          <div className="info-group">
            <label>{isRoleCarrier ? "Raised by" : "Owner assigned"}</label>
            <div className="readonly-box">
              {isRoleCarrier
                ? displayRaisedBy(currentViewClaim)
                : displayClaimOwner(currentViewClaim.owner)}
            </div>
          </div>
          <div className="info-group">
            <label>From</label>
            <div className="readonly-box">{toDisplayDate(currentViewClaim.fromDate) || "N/A"}</div>
          </div>
          <div className="info-group">
            <label>Shipment number</label>
            <div className="readonly-box">{currentViewClaim.shipment}</div>
          </div>
          <div className="info-group">
            <label>To</label>
            <div className="readonly-box">{toDisplayDate(currentViewClaim.toDate) || "N/A"}</div>
          </div>
          <div className="info-group">
            <label>Priority</label>
            <div className="readonly-box">{currentViewClaim.priority}</div>
          </div>
          <div className="info-group">
            <label>Anomaly</label>
            <div className="readonly-box">{currentViewClaim.category}</div>
          </div>
          {isRoleStaff && (
            <div className="info-group">
              <label>Status</label>
              <div className="readonly-box">{currentViewClaim.status || "Pending"}</div>
            </div>
          )}
        </div>

        <div className="tabs-section">
          <div className="tab-buttons">
            <button className={activeTab === "Description" ? "active" : ""} onClick={() => setActiveTab("Description")}>Description</button>
            <button className={activeTab === "Expected actions" ? "active" : ""} onClick={() => setActiveTab("Expected actions")}>Expected actions</button>
            <button className={activeTab === "Root cause analysis" ? "active" : ""} onClick={() => setActiveTab("Root cause analysis")}>Root cause analysis</button>
            <button className={activeTab === "Corrective actions" ? "active" : ""} onClick={() => setActiveTab("Corrective actions")}>Corrective actions</button>
          </div>
          <div className="tab-content-box">
            {activeTab === "Description" && (currentViewClaim.description || "No description provided")}
            {activeTab === "Expected actions" && (currentViewClaim.expectedActions || "N/A")}
            {activeTab === "Root cause analysis" && (currentViewClaim.rootCauseAnalysis || "N/A")}
            {activeTab === "Corrective actions" && (currentViewClaim.correctiveActions || "N/A")}
          </div>
        </div>

        <div className="cmr-remark">
          <span>CMR Remark</span>
          <div className="check-group">
            <input type="checkbox" readOnly checked={currentViewClaim.cmrRemark === true} /> Yes
          </div>
          <div className="check-group">
            <input type="checkbox" readOnly checked={currentViewClaim.cmrRemark !== true} /> No
          </div>
        </div>

        <div className="files-section">
          {currentViewClaim.files && currentViewClaim.files.length > 0 ? (
            currentViewClaim.files.map((f, i) => (
              <a
                key={i}
                href={getClaimFileUrl(f)}
                target="_blank"
                rel="noopener noreferrer"
                className="file-pill file-pill-link"
              >
                {getClaimFileLabel(f)}
              </a>
            ))
          ) : null}
        </div>

        {isAdmin && currentViewClaim.platformResponse && (
          <div className="platform-section">
            <label>Platform response</label>
            <div className="readonly-box" style={{ minHeight: "60px", borderRadius: "12px" }}>
              {currentViewClaim.platformResponse}
            </div>
          </div>
        )}

        {/* View mode: Assign to is read-only — editable only in Edit */}
        {showAssignInfo && (
          <div className="platform-section">
            <label>Assign to</label>
            <div className="readonly-box">{displayAssignToLabel(assignToValue)}</div>
            {assignToValue === "Platform" && currentViewClaim.staffAccountNumber && (
              <>
                <label style={{ marginTop: "16px" }}>Staff account number</label>
                <div className="readonly-box">{currentViewClaim.staffAccountNumber}</div>
              </>
            )}
          </div>
        )}

        <div className="form-actions mt-4">
          <button type="button" className="outline-btn" onClick={goBackToList}>
            <FiArrowLeft size={16} /> Back
          </button>
          <button
            className="outline-btn"
            onClick={() => canEditClaimItem(currentViewClaim) && handleEditClaim(currentViewClaim)}
            disabled={!canEditClaimItem(currentViewClaim)}
          >
            Modify
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="d-flex" id="wrapper">
      <SideBar />
      {filterOpen && (
        <ClaimsFilterSidebar
          close={handleFilterToggle}
          handleFilterFun={handleSideFilter}
          partyFilterLabel={isRoleClient ? "Owner" : "Raised by"}
          partyFilterPlaceholder={
            isRoleClient ? "Platform, Carrier…" : "Shipper, Platform…"
          }
        />
      )}
      <div id="page-content-wrapper">
        <div className="topheader">
          <div className="container-fluid">
            <div className="topheader_flex">
              <Button
                variant="primary"
                className="menuBtn"
                onClick={toggleBodyClass}
              >
                <FiMenu />
              </Button>
              <div className="inPutBox"></div>
              <Link className="notiIcon">
                <Image src={NotificationIcon} alt="" />
                <Badge bg="danger">3</Badge>
              </Link>
            </div>
          </div>
        </div>
        
        <div className="claims-page">
          <Container fluid>
            {view === "list" && (
              <>
                {/* Stats section */}
                <div className="stats-container">
                  <div className="stat-card total">
                    <h4>Total Claims</h4>
                    <h2>{stats.totalClaims.toString().padStart(2, "0")}</h2>
                  </div>
                  <div className="stat-card resolved">
                    <h4>Resolved</h4>
                    <h2>{stats.resolved.toString().padStart(2, "0")}</h2>
                  </div>
                  <div className="stat-card pending">
                    <h4>Pending</h4>
                    <h2>{stats.pending.toString().padStart(2, "0")}</h2>
                  </div>
                  <div className="stat-card high-priority">
                    <h4>High Priority</h4>
                    <h2>{stats.highPriority.toString().padStart(2, "0")}</h2>
                  </div>
                </div>

                {/* Table actions section */}
                <div className="table-actions">
                  <div className="left-actions">
                    {canCreateClaim && (
                      <button className="new-claim-btn" onClick={() => {
                        setEditingClaimId(null);
                        setFormData(emptyClaimForm());
                        setView("new");
                      }}>
                        <FiPlusCircle size={18} /> New Claim
                      </button>
                    )}
                    <div className="search-box">
                      <input
                        type="text"
                        placeholder="search"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                      <Image src={SearchIcon} alt="search" />
                    </div>
                  </div>
                  <div className="right-actions">
                    <button
                      type="button"
                      className="toolbar-action-btn"
                      onClick={toggleExportMode}
                    >
                      <Image src={ExportIcon} width={18} alt="" /> Export
                    </button>
                    <button
                      type="button"
                      className="toolbar-action-btn"
                      onClick={handleFilterToggle}
                    >
                      <Image src={FilterIcon} width={18} alt="" /> Filters
                    </button>
                  </div>
                </div>

                {exportMode && (
                  <div className="exportStrip">
                    <div className="d-flex align-items-center">
                      <IoIosCloseCircleOutline
                        onClick={toggleExportMode}
                        style={{ cursor: "pointer" }}
                      />
                      <p className="ms-2">
                        <strong>{selectedClaimsCount}</strong> element selected
                      </p>
                    </div>
                    <button type="button" onClick={handleExportClaimsToExcel}>
                      Export to Excel
                    </button>
                  </div>
                )}

                {/* Table section */}
                <div className="claims-table-container">
                  <table>
                    <thead>
                      <tr>
                        {exportMode && (
                          <th className="export-check-col">
                            <Form.Check
                              type="checkbox"
                              label="All"
                              checked={allVisibleClaimsSelected}
                              onChange={(e) => handleSelectAllClaims(e.target.checked)}
                            />
                          </th>
                        )}
                        <th className="row-index"></th>
                        <th>REFERENCE</th>
                        <th>STATUS</th>
                        <th>DATE</th>
                        <th>CATEGORY</th>
                        <th>SHIPMENT</th>
                        <th>{listPartyColumnLabel}</th>
                        <th>PRIORITY</th>
                        <th>ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredClaims.map((clm, index) => (
                        <tr key={clm._id || index}>
                          {exportMode && (
                            <td className="export-check-col">
                              <Form.Check
                                type="checkbox"
                                checked={selectedClaimIds.includes(clm._id)}
                                onChange={() => handleSelectClaimRow(clm._id)}
                              />
                            </td>
                          )}
                          <td className="row-index">{index + 1}</td>
                          <td style={{ color: "#4f5b92", fontWeight: 600 }}>{renderClaimReference(clm.reference)}</td>
                          <td>
                            <span className={`status-badge ${clm.status.toLowerCase()}`}>
                              {clm.status}
                            </span>
                          </td>
                          <td style={{ color: "#4f5b92", fontWeight: 600 }}>{toDisplayDate(clm.date) || clm.date}</td>
                          <td style={{ color: "#4f5b92", fontWeight: 600 }}>{clm.category}</td>
                          <td style={{ color: "#4f5b92", fontWeight: 600 }}>{clm.shipment}</td>
                          <td style={{ color: "#4f5b92", fontWeight: 600 }}>{displayListPartyColumn(clm)}</td>
                          <td>
                            <span className={`priority-badge ${clm.priority.toLowerCase()}`}>
                              {clm.priority}
                            </span>
                          </td>
                          <td>
                            <Dropdown align="end" className="action-dropdown">
                              <Dropdown.Toggle
                                variant="link"
                                id={`claim-actions-${clm._id || index}`}
                                className="claims-row-actions"
                              >
                                <FiMoreVertical size={18} color="#4f5b92" />
                              </Dropdown.Toggle>
                              <Dropdown.Menu>
                                <Dropdown.Item onClick={() => handleViewClaim(clm)}>
                                  <FaRegEye /> View
                                </Dropdown.Item>
                                {canEditClaimItem(clm) && (
                                  <>
                                    <Dropdown.Item onClick={() => handleEditClaim(clm)}>
                                      <FaRegEdit /> Edit
                                    </Dropdown.Item>
                                    {canManageClaims && (
                                      <Dropdown.Item onClick={() => handleDeleteClaim(clm._id)} className="text-danger">
                                        <FaRegTrashAlt /> Delete
                                      </Dropdown.Item>
                                    )}
                                  </>
                                )}
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      ))}
                      {filteredClaims.length === 0 && (
                        <tr>
                          <td colSpan={exportMode ? 10 : 9} className="text-center py-4">
                            No claims found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {view === "view" && renderViewClaim()}

            {view === "new" && (canCreateClaim || editingClaimId) && (
              <div className="new-claim-container">
                <div className="header-info">
                  <h3 className="claim-ref">
                    Claim Number :{" "}
                    <span>
                      {editingClaimId
                        ? renderClaimReference(
                            currentViewClaim?.reference ||
                              claims.find((c) => c._id === editingClaimId)?.reference
                          )
                        : renderClaimReference(getNextClaimReferencePreview(claims))}
                    </span>
                  </h3>
                  <h3 className="claim-date">
                    Date : <span>{editingClaimId ? (toDisplayDate(currentViewClaim?.date) || todayDisplayDate()) : todayDisplayDate()}</span>
                  </h3>
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label>Shipment</label>
                    <input
                      type="text"
                      name="shipment"
                      value={formData.shipment}
                      onChange={handleInputChange}
                      onBlur={handleShipmentLookup}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleShipmentLookup();
                        }
                      }}
                      placeholder="Enter shipment number"
                      disabled={isClaimMetaLocked}
                      readOnly={isClaimMetaLocked}
                      className={isClaimMetaLocked ? "field-locked" : ""}
                    />
                  </div>
                  <div className="form-group">
                    <label>From</label>
                    <div className="date-input">
                      <input
                        type="date"
                        name="fromDate"
                        value={formData.fromDate}
                        onChange={handleInputChange}
                        disabled={isClaimMetaLocked}
                        readOnly={isClaimMetaLocked}
                        className={isClaimMetaLocked ? "field-locked" : ""}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    {isRoleCarrier ? (
                      <PillSelect
                        label="Raised by"
                        name="raisedBy"
                        value={
                          displayRaisedBy(currentViewClaim || formData) ===
                          "Platform"
                            ? "Platform"
                            : "Shipper"
                        }
                        onChange={() => {}}
                        options={[
                          { value: "Shipper", label: "Shipper" },
                          { value: "Platform", label: "Platform" },
                        ]}
                        disabled
                      />
                    ) : (
                      <PillSelect
                        label="Owner assigned"
                        name="owner"
                        value={formData.owner}
                        onChange={handleInputChange}
                        options={getOwnerOptions()}
                        disabled={isClaimMetaLocked}
                      />
                    )}
                  </div>
                  <div className="form-group">
                    <label>To</label>
                    <div className="date-input">
                      <input
                        type="date"
                        name="toDate"
                        value={formData.toDate}
                        onChange={handleInputChange}
                        disabled={isClaimMetaLocked}
                        readOnly={isClaimMetaLocked}
                        className={isClaimMetaLocked ? "field-locked" : ""}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Anomaly Category</label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      disabled={isClaimMetaLocked}
                      className={isClaimMetaLocked ? "field-locked" : ""}
                    >
                      {ANOMALY_CATEGORIES.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Priority</label>
                    <select
                      name="priority"
                      value={formData.priority}
                      onChange={handleInputChange}
                      disabled={isClaimMetaLocked}
                      className={isClaimMetaLocked ? "field-locked" : ""}
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="Critical">Critical</option>
                    </select>
                  </div>
                  {isRoleStaff && (
                    <div className="form-group">
                      <label>Status</label>
                      <select
                        name="status"
                        value={formData.status || "Pending"}
                        onChange={handleInputChange}
                      >
                        {CLAIM_STATUS_OPTIONS.map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div className="tabs-container">
                  <div className="tabs-header">
                    {["Description", "Expected actions", "Root cause analysis", "Corrective actions"].map(
                      (tab) => {
                        const isOpenerTab =
                          tab === "Description" || tab === "Expected actions";
                        const isReadOnly = isOpenerTab
                          ? !canEditOpenerSections
                          : !canEditReceiverSections;
                        return (
                          <button
                            key={tab}
                            type="button"
                            className={`${activeTab === tab ? "active" : ""}${isReadOnly ? " tab-readonly" : ""}`}
                            onClick={() => handleTabChange(tab)}
                            title={
                              isReadOnly
                                ? "View only — you cannot edit this section"
                                : undefined
                            }
                          >
                            {tab}
                          </button>
                        );
                      }
                    )}
                  </div>
                  <div className="tab-content">{renderActiveTabContent()}</div>
                  <p className="sections-permission-hint">
                    {canEditReceiverSections
                      ? "You can view Description and Expected actions (read-only). Fill Root cause analysis and Corrective actions."
                      : "You can fill Description and Expected actions. Root cause and Corrective actions are for the Carrier (view only for you)."}
                  </p>
                </div>

                <div className="cmr-remark">
                  <label>CMR Remark</label>
                  <Form.Check
                    inline
                    label="Yes"
                    name="cmrRemark"
                    type="radio"
                    checked={formData.cmrRemark === true}
                    disabled={canEditReceiverSections}
                    onChange={() => setFormData((prev) => ({ ...prev, cmrRemark: true }))}
                  />
                  <Form.Check
                    inline
                    label="No"
                    name="cmrRemark"
                    type="radio"
                    checked={formData.cmrRemark === false}
                    disabled={canEditReceiverSections}
                    onChange={() => setFormData((prev) => ({ ...prev, cmrRemark: false }))}
                  />
                </div>

                <div className="file-upload-section">
                  <div className="uploaded-files">
                    {formData.files && formData.files.map((f, i) => renderClaimFileBadge(f, i, { removable: !canEditReceiverSections }))}
                  </div>
                  <input type="file" multiple ref={fileInputRef} style={{ display: "none" }} onChange={handleFileChange} />
                  {!canEditReceiverSections && (
                    <button type="button" className="add-file-btn" onClick={() => fileInputRef.current.click()}>
                      <FiPlusCircle /> Add File
                    </button>
                  )}
                </div>

                {localStorage.getItem("access") && localStorage.getItem("access").includes("adminAccess") && (
                  <div className="platform-section" style={{ borderTop: "1px solid #f1f2f6", paddingTop: "30px", marginBottom: "10px" }}>
                    <label style={{ display: "block", color: "#4f5b92", fontWeight: 600, marginBottom: "10px", fontSize: "14px" }}>Platform response</label>
                    <textarea
                      name="platformResponse"
                      placeholder="Claim has been reviewed and initiated"
                      value={formData.platformResponse || ""}
                      onChange={handleInputChange}
                      style={{ width: "100%", borderRadius: "12px", border: "1px solid #e1e5ee", padding: "15px", fontSize: "14px", color: "#4f5b92", marginBottom: "20px", resize: "vertical", minHeight: "80px" }}
                    ></textarea>
                  </div>
                )}

                {isRoleStaff && (
                  <div className="platform-section" style={{ borderTop: "1px solid #f1f2f6", paddingTop: "10px", marginBottom: "30px" }}>
                    <PillSelect
                      label="Assign to"
                      name="assignTo"
                      value={formData.assignTo || "Platform"}
                      onChange={handleInputChange}
                      options={getAssignToOptions(formData.owner)}
                      className="claims-pill-select"
                    />
                    {renderStaffAccountField()}
                    {editingClaimId &&
                      canTransferToCarrier(formData.owner) &&
                      formData.assignTo === "Platform" && (
                      <p style={{ marginTop: "10px", fontSize: "12px", color: "#7f8fa6" }}>
                        Pending Platform review — set Assign to <strong>Carrier</strong> to transfer this claim.
                      </p>
                    )}
                  </div>
                )}

                {isRoleCarrier && editingClaimId && (
                  <div className="platform-section" style={{ borderTop: "1px solid #f1f2f6", paddingTop: "30px", marginBottom: "30px" }}>
                    <PillSelect
                      label="Assign to"
                      name="assignTo"
                      value="Platform"
                      onChange={handleInputChange}
                      options={getAssignToOptions(formData.owner)}
                      className="claims-pill-select"
                    />
                    <p style={{ marginTop: "10px", fontSize: "12px", color: "#7f8fa6", maxWidth: "420px" }}>
                      Option: <strong>Platform</strong> only.
                    </p>
                  </div>
                )}

                <div className="action-footer">
                  {saveError && (
                    <p style={{ color: "#e74c3c", fontSize: "14px", fontWeight: 600, marginBottom: "12px", width: "100%" }}>
                      {saveError}
                    </p>
                  )}
                  <button
                    type="button"
                    className="delete-btn"
                    onClick={() => {
                      if (editingClaimId && currentViewClaim) {
                        setEditingClaimId(null);
                        setView("view");
                      } else {
                        setEditingClaimId(null);
                        setView("list");
                      }
                    }}
                  >
                    <FiArrowLeft size={16} /> Back
                  </button>
                  <button className="save-btn" onClick={handleSaveClaim} disabled={isSaving}>
                    {isSaving ? "Saving..." : "Save information"}
                  </button>
                </div>
              </div>
            )}
          </Container>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={4000} />
    </div>
  );
};

export default Claims;
